import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Check, X } from "lucide-react";
import { PageHero, CTABand } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { products } from "@/data/products";

const tabs = ["All", "Hardware", "Software"];

const ProductDialog = ({ product, onClose }) => (
  <AnimatePresence>
    {product && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="product-dialog"
        className="fixed inset-0 z-[80] grid place-items-center p-4 sm:p-8"
      >
        <div
          className="absolute inset-0 bg-das-ink/60 backdrop-blur-sm"
          onClick={onClose}
          data-testid="product-dialog-overlay"
        />
        <motion.div
          initial={{ opacity: 0, y: 48, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 32, scale: 0.97 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          data-lenis-prevent
          className="relative grid max-h-[86vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] bg-white md:grid-cols-2"
        >
          <div className="relative min-h-64">
            <img
              src={product.image}
              alt={product.name}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute left-5 top-5 rounded-full bg-white/85 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-das-deep backdrop-blur">
              {product.category}
            </span>
          </div>
          <div className="p-8 sm:p-10">
            <h3 className="font-display text-3xl font-semibold tracking-tight text-das-ink">
              {product.name}
            </h3>
            <p className="mt-1 font-display text-sm font-semibold text-das-violet">
              {product.tagline}
            </p>
            <p className="mt-5 text-sm leading-relaxed text-neutral-600">{product.description}</p>
            <ul className="mt-6 space-y-2.5">
              {product.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-neutral-700">
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-das-violet/10">
                    <Check className="h-3 w-3 text-das-deep" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <a
              href="/consultation"
              data-testid="product-dialog-enquire-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-das-violet px-7 py-3.5 font-display text-sm font-semibold text-white transition-colors duration-300 hover:bg-das-deep"
            >
              Enquire about this product
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <button
            onClick={onClose}
            data-testid="product-dialog-close"
            aria-label="Close product details"
            className="absolute right-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/90 text-das-ink backdrop-blur transition-colors hover:bg-das-violet hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default function Products() {
  const [tab, setTab] = useState("All");
  const [active, setActive] = useState(null);

  const filtered = useMemo(
    () => (tab === "All" ? products : products.filter((p) => p.category === tab)),
    [tab]
  );

  return (
    <div data-testid="products-page">
      <PageHero
        overline="Our Products"
        lines={["A toolkit for every", "till, table & terminal"]}
        copy="From flagship Android POS devices to cloud payment platforms — explore the hardware and software powering faster payments across South Africa and Botswana."
        testid="products-hero"
      />

      <section className="mx-auto max-w-7xl px-6 pb-28 md:px-12">
        <Reveal>
          <div
            className="mb-14 inline-flex rounded-full border border-neutral-200 bg-white p-1.5"
            data-testid="products-filter-tabs"
          >
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`products-tab-${t.toLowerCase()}`}
                className={`relative rounded-full px-6 py-2.5 font-display text-sm font-semibold transition-colors duration-300 ${
                  tab === t ? "text-white" : "text-neutral-500 hover:text-das-ink"
                }`}
              >
                {tab === t && (
                  <motion.span
                    layoutId="products-tab-pill"
                    className="absolute inset-0 rounded-full bg-das-violet"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                <span className="relative">{t}</span>
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.button
                layout
                key={p.id}
                type="button"
                onClick={() => setActive(p)}
                data-testid={`product-card-${p.id}`}
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group relative overflow-hidden rounded-[1.75rem] border border-neutral-200 bg-white text-left transition-all duration-500 hover:-translate-y-2 hover:border-das-violet/40 hover:shadow-lift"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-white/85 px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-das-deep backdrop-blur">
                    {p.category}
                  </span>
                </div>
                <div className="flex items-start justify-between gap-4 p-7">
                  <div>
                    <h3 className="font-display text-xl font-semibold tracking-tight text-das-ink">
                      {p.name}
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500">{p.tagline}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {p.features.slice(0, 2).map((f) => (
                        <span
                          key={f}
                          className="rounded-full bg-neutral-100 px-3 py-1 text-[11px] font-semibold text-neutral-600"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span className="mt-1 grid h-11 w-11 shrink-0 place-items-center rounded-full border border-neutral-200 text-das-ink transition-all duration-300 group-hover:border-das-violet group-hover:bg-das-violet group-hover:text-white">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <ProductDialog product={active} onClose={() => setActive(null)} />

      <CTABand
        title="Not sure which device fits?"
        copy="Tell us how you trade — counter, mobile, self-service or online — and we'll match the right hardware and software to your business."
        testid="products-cta-band"
      />
    </div>
  );
}
